"use client";

import { useEffect } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import FrameConfig from "@lib/classes/FrameConfig";

/**
 * Syncs URL params with FrameConfig sessionStorage
 * Ensures URL is the source of truth for language, override, and bridge routing params
 */
export default function FrameConfigSync() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const frameConfig = new FrameConfig();

    // Get values from URL
    const urlLang = searchParams.get("lang") || searchParams.get("locale");
    const urlOverride = searchParams.get("or") || searchParams.get("override");
    const urlBridgeId = searchParams.get("bridge_id");
    const urlBridgeType = searchParams.get("bridge_type");

    // URL parameters take precedence - always sync sessionStorage with URL when they
    // differ. Language and override are only SAVED, never followed by an Apollo store
    // reset: save() dispatches frameConfigChange, and ApolloProvider rebuilds the
    // client — fresh cache, new request headers — whenever the saved value changes. A
    // reset on the outgoing client would only kill the page's own in-flight
    // config/content queries (Apollo "Store reset while query was in flight", #34).
    if (urlLang && urlLang !== frameConfig.language) {
      frameConfig.language = urlLang;
      window.language = urlLang;
      frameConfig.save();
    }

    // Sync override - URL takes precedence only when present
    if (urlOverride !== null && urlOverride !== frameConfig.override) {
      frameConfig.override = urlOverride;
      frameConfig.save();
    }
    

    // Sync bridge_id - URL takes precedence only when present
    if (urlBridgeId) {
      const parsedBridgeId = parseInt(urlBridgeId, 10);
      if (!Number.isNaN(parsedBridgeId) && parsedBridgeId !== frameConfig.bridgeId) {
        frameConfig.bridgeId = parsedBridgeId;
        frameConfig.save();
        window.apolloClient?.resetStore();
      }
    }

    // Sync bridge_type - URL takes precedence only when present
    if (urlBridgeType) {
      const parsedBridgeType = parseInt(urlBridgeType, 10);
      if (!Number.isNaN(parsedBridgeType) && parsedBridgeType !== frameConfig.bridgeType) {
        frameConfig.bridgeType = parsedBridgeType;
        frameConfig.save();
        window.apolloClient?.resetStore();
      }
    }

    // If no language in URL but one exists in sessionStorage, add it to URL
    // This handles the case where user navigates to a URL without lang param
    if (!urlLang && frameConfig.language && frameConfig.language !== "en") {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set("lang", frameConfig.language);
      
      const queryString = newParams.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
      
      router.replace(newUrl);
    }
  }, [searchParams, router, pathname]);

  return null;
}